<template>
  <div v-if="kind !== 'HotSpot'" class="h-100 w-100">
    <b-container :class="{ c_4qv: kind === '4QV' }" class="h-100 w-100">
      <vue-form-generator
        :schema="schema"
        :model="model"
        :options="{ validationAfterLoad: true, validationAfterChanged: true }"
        :class="['h-100', { vfg_4qv: kind === '4QV', 'answered': correction }]"
      />
      <div v-if="kind === '4QV'" class="image">
        <img :src="imgUrl" class="image-4Q">
      </div>
    </b-container>
  </div>
  <div v-else-if="kind === 'HotSpot'" class="h-100 d-flex align-items-center justify-content-center w-100">
    <div ref="div-canvas" class="img-hotspot">
      <canvas
        ref="canvas"
        :class="{ 'answered': correction }"
        :height="canvasProperty.height"
        :width="canvasProperty.width"
        :style="{ 'background-image': `url(${imgUrl})`}"
        @click="onClickOnCanvas"
      />
    </div>
  </div>
</template>

<script>
  import VueFormGenerator from '@triotech/vue-core/src/vendor/vue-form-generator';
  import fieldRadios from '@/vendor/vue-form-generator/fields/fieldRadios';

  VueFormGenerator.component.components.formGroup.components.fieldRadios = fieldRadios;

  export default {
    name: 'AnswerKindComponent',
    components: {
      'vue-form-generator': VueFormGenerator.component,
    },
    props: {
      answers: {
        type: Array,
        default: null,
      },
      kind: {
        type: String,
        default: null,
      },
      image: {
        type: Object,
        default: null,
      },
      correction: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        isReady: false,
        model: {
          action: 'validate',
          answer: [],
        },
        schema: {
          fields: [{
            id: 'answer',
            type: 'radios',
            model: 'answer',
            required: true,
            values: [],
          }],
        },
        canvasProperty: {
          height: 100,
          width: 100,
          ctx: null,
          crosshairImg: null,
        },
        hotspotImgRatio: 1,
      };
    },
    computed: {
      imgUrl() {
        if (this.image) {
          return this.$helper.getImage(this.image.upload_file_path);
        }
        return false;
      },
      getDivCanvasSize() {
        let width = 0;
        let height = 0;
        if (this.$refs['div-canvas']) {
          width = this.$refs['div-canvas'].clientWidth;
          height = this.$refs['div-canvas'].clientHeight;
        }
        return { width, height };
      },
    },
    watch: {
      model: {
        deep: true,
        handler(newModel) {
          this.$bus.$emit('model-updated', newModel);
        },
      },
    },
    mounted() {
      this.getAnswers();
    },
    updated() {
      if (this.canvasProperty.ctx) {
        this.clearRect();
      }
      this.getAnswers();

      if (!this._.isNull(this.correction) && this.kind === 'HotSpot') {
        this.showHotSpotAnswer();
      }
    },
    methods: {
      getAnswers() {
        if (this.kind !== 'HotSpot') {
          this.schema.fields[0].values = [];
          this._.each(this.answers, (answer) => {
            this.schema.fields[0].values.push({
              name: answer.value,
              value: answer.id,
              disabled: !this._.isNull(this.correction),
              correct: this.isCorrectAnswer(answer),
              error: this.isErrorAnswer(answer),
            });
          });

          if (!this._.isNull(this.correction)) {
            this.model.answer = parseInt(this.correction.user, 10);
          }
        } else {
          const img = new Image();

          img.addEventListener('load', () => {
            const newSize = this.getCanvasSize(img);
            this.$set(this.canvasProperty, 'height', newSize.height);
            this.$set(this.canvasProperty, 'width', newSize.width);
          }, false);

          img.src = this.imgUrl;

          if (this.$refs.canvas) {
            this.$set(this.canvasProperty, 'ctx', this.$refs.canvas.getContext('2d'));
          }
        }
      },
      isCorrectAnswer(answer) {
        return !this._.isNull(this.correction) ? parseInt(this.correction.correct, 10) === answer.id : null;
      },
      isErrorAnswer(answer) {
        if (!this._.isNull(this.correction)) {
          const userAnswer = parseInt(this.correction.user, 10);
          return userAnswer === answer.id && userAnswer !== parseInt(this.correction.correct, 10);
        }
        return null;
      },
      getCrossHair() {
        return new Promise((resolve, reject) => {
          if (this.canvasProperty.crosshairImg) {
            resolve();
          } else {
            const img = new Image();
            img.addEventListener('load', () => {
              this.canvasProperty.crosshairImg = img;
              resolve();
            });
            img.addEventListener('error', reject);
            img.src = '/static/img/crosshair.svg';
          }
        });
      },
      getCanvasSize(img) {
        // one side or more is over sized, return the minus ratio (with max ratio = 1)
        const ratioWidth = this.getDivCanvasSize.width / img.width;
        const ratioHeight = this.getDivCanvasSize.height / img.height;
        this.hotspotImgRatio = Math.min(1, Math.min(ratioWidth, ratioHeight));

        return { width: img.width * this.hotspotImgRatio, height: img.height * this.hotspotImgRatio };
      },
      showHotSpotAnswer() {
        const { ctx } = this.canvasProperty;
        ctx.height = this.canvasProperty.height;
        ctx.width = this.canvasProperty.width;
        const userAnswer = this.correction.user.split(':');

        this._.each(this.correction.correct, (correct) => {
          const scaledCorrect = this._.mapValues(correct, value => (this._.isNumber(value) ? value * this.hotspotImgRatio : value));
          ctx.rect(scaledCorrect.x, scaledCorrect.y, scaledCorrect.width, scaledCorrect.height);
          ctx.fillStyle = 'rgba(50,205,50,.5)';
        });
        ctx.fill();

        this.drawCrossHair(userAnswer[0], userAnswer[1], true);
      },
      async drawCrossHair(x, y, scale = false) {
        await this.getCrossHair();
        const crossHair = this.canvasProperty.crosshairImg;
        const { width, height } = crossHair;
        const scaledCoords = this._.mapValues({
          x: x - width / 2, y: y - height / 2,
        }, value => value * (scale ? this.hotspotImgRatio : 1));
        this.canvasProperty.ctx.drawImage(crossHair, scaledCoords.x, scaledCoords.y, width, height);
      },
      onClickOnCanvas(event) {
        if (!this._.isNull(this.correction)) {
          return false;
        }

        this.clearRect();
        this.drawCrossHair(event.offsetX, event.offsetY);
        this.model.answer = [];

        // fix value, Math.floor can return 1px over img size.
        const screenOffsetX = Math.min(
          Math.floor(event.offsetX / this.hotspotImgRatio),
          Math.floor(this.canvasProperty.width / this.hotspotImgRatio),
        );
        const screenOffsetY = Math.min(
          Math.floor(event.offsetY / this.hotspotImgRatio),
          Math.floor(this.canvasProperty.height / this.hotspotImgRatio),
        );

        this.model.answer.push({ x: screenOffsetX, y: screenOffsetY });
        return true;
      },
      clearRect() {
        this.canvasProperty.ctx.clearRect(0, 0, this.canvasProperty.width, this.canvasProperty.height);
        this.canvasProperty.ctx.beginPath();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@/scss/vars';

  .img-hotspot {
    height: 90%;
    max-height: 580px;
    width: 90%;
    max-width: 940px;
  }

  canvas {
    display: block;
    background-size: cover;
    margin: auto;

    &:not(.answered) {
      cursor: crosshair;
    }
  }

  .vfg_4qv {
    width: 25%;
  }

  .c_4qv {
    display: flex;
    justify-content: space-around;
  }

  .image-4Q {
    max-height: 95%;
    margin-left: 10px;
  }

  .image {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /deep/ .c_4qv {
    fieldset {
      .radio-list {
        flex-direction: column;
        label {
          height: calc(25% - 20px);
          width: 100%;
        }
      }
    }
  }

  .vue-form-generator:not(.answered) /deep/ label {
    cursor: pointer;
  }

  /deep/ fieldset {
    height: 100%;

    .field-radios,
    .field-wrap {
      height: 100%;
    }

    .radio-list {
      height: 100%;
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      label {
        padding: 20px;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        width: calc(50% - 20px);
        height: calc(50% - 20px);
        background: $white;
        border: 1px solid $primary;
        color: $primary;
        margin: 10px;

        &.is-checked {
          background-color: $primary;
          color: $white;
        }

        &.is-error {
          border: 4px solid $red;
        }

        &.is-correct {
          border: 4px solid $green;
        }

        input {
          display: none;
        }
      }
    }
  }
</style>
